import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Building2, MessageCircle, Award, Gift, Stethoscope, ArrowRight, Lock, CheckCircle, XCircle, Wallet, BookOpen, CalendarDays, Copy, Check, ChevronDown, Bell, Play } from 'lucide-react';
import { api, ApiError, type Subscription, type Booking, type Consultation, type Certificate, type ReferralSummary, type Plan, type Doctor, egp, formatBookingDays, type BlogPost, type EventItem } from '../api';
import { PageHeader, Card, StatusBadge, Button, Alert, PageLoader, Input, Textarea, Table } from '../components/ui';
import { InlinePlans } from '../components/InlinePlans';
import { SmartCoverMedia } from '../components/SmartCoverMedia';
import { WhatsAppHelp, useSupportPhone } from '../components/WhatsAppHelp';
import { RequirePhotoModal } from '../components/RequirePhoto';
import { PendingApprovalModal } from '../components/PendingApprovalModal';
import { PlanOfferBadge } from '../components/PlanOfferBadge';
import { pendingApprovalPromptIsOpen, useAuth } from '../auth';
import { FormattedText } from '../lib/simpleMarkup';
import { applyDiscount, toWhatsAppUrl } from '@physioedge/shared';

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        {children}
      </div>
    </div>
  );
}

// ─── Tab content components ───────────────────────────────────────────────────

function formatPlanExpiry(iso: string) {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function missedFirstWeek(sub: Subscription) {
  return sub.status === 'expired' && !sub.startDate;
}

function waitingToStart(sub: Subscription) {
  return (sub.status === 'active' || sub.status === 'pending') && !sub.startDate;
}

function planTimingLabel(sub: Subscription) {
  if (sub.status === 'frozen') return 'Paused until';
  if (missedFirstWeek(sub)) return 'Booking locked';
  if (waitingToStart(sub)) return 'Book first week by';
  return 'Ends';
}

function planTimingValue(sub: Subscription) {
  if (missedFirstWeek(sub)) return 'Message us';
  const by = sub.planClock?.mustBookFirstWeekBy ?? sub.firstBookDeadline;
  if (waitingToStart(sub) && by) return formatPlanExpiry(by);
  if (sub.expiryDate) return formatPlanExpiry(sub.expiryDate);
  return '—';
}

function StudentBookingCard({ booking: b }: { booking: Booking }) {
  const qc = useQueryClient();
  const [openRate, setOpenRate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    trainingQuality: '4',
    doctorSupport: '4',
    learningExperience: '4',
    comment: '',
  });

  const submitRating = async () => {
    setSaving(true);
    setMsg('');
    try {
      await api.post(`/api/bookings/${b.id}/rating`, {
        trainingQuality: Number(form.trainingQuality),
        doctorSupport: Number(form.doctorSupport),
        learningExperience: Number(form.learningExperience),
        ...(form.comment ? { comment: form.comment } : {}),
      });
      qc.invalidateQueries({ queryKey: ['student-bookings'] });
      setOpenRate(false);
      setMsg('Thanks for rating this clinic.');
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Failed to submit rating.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-ink truncate">{b.clinicName ?? b.locationName ?? 'Internship'}</div>
          {b.locationName && b.clinicName && (
            <div className="text-xs text-ink-soft truncate">{b.locationName}</div>
          )}
          <div className="text-xs text-ink-soft">{formatBookingDays(b)} · {b.slotStartTime}–{b.slotEndTime}</div>
        </div>
        <StatusBadge status={b.status} />
      </div>

      {b.status !== 'cancelled' && (
        <div className="text-sm space-y-1.5 rounded-radius-sm border border-line bg-bg px-3 py-2">
          {b.mapUrl && (
            <a href={b.mapUrl} target="_blank" rel="noreferrer" className="text-sand-strong font-medium hover:underline inline-flex items-center gap-1">
              Open map
            </a>
          )}
          {b.staffPhones && b.staffPhones.length > 0 ? (
            <div className="space-y-1">
              {b.staffPhones.map(phone => {
                const wa = toWhatsAppUrl(phone);
                return (
                  <div key={phone} className="text-ink">
                    Staff: <a href={`tel:${phone}`} className="font-medium text-teal">{phone}</a>
                    {wa && (
                      <>
                        {' · '}
                        <a href={wa} target="_blank" rel="noreferrer" className="font-medium text-sand-strong hover:underline">WhatsApp</a>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : b.staffPhones ? (
            <div className="text-ink-soft text-xs">No staff number for this branch yet.</div>
          ) : (
            <div className="text-ink-soft text-xs">Staff number 1 day before you start.</div>
          )}
          <p className="text-xs text-ink-soft">
            {b.hasCertificate
              ? 'Your certificate is ready in Certificates below.'
              : 'You get a certificate after the clinic confirms you finished.'}
          </p>
        </div>
      )}

      {b.status !== 'cancelled' && typeof b.weekPercent === 'number' && (
        <div className="text-sm rounded-radius-sm border border-line bg-bg px-3 py-2 space-y-1">
          <div className="font-medium text-ink">Week {b.weekPercent}%</div>
          {b.phase === 'in_progress' && (
            <p className="text-xs text-ink-soft">Finish the week, then you can rate this clinic.</p>
          )}
          {(b.attendanceDays ?? []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {(b.attendanceDays ?? []).map(day => (
                <span
                  key={day.date}
                  className={`text-[11px] px-2 py-1 rounded-radius-sm border ${
                    day.attended
                      ? 'border-line bg-surface text-ink'
                      : 'border-line text-ink-faint'
                  }`}
                >
                  {new Date(`${day.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}
                  {day.attended ? ' · in' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {b.evaluation && (
        <div className="text-sm rounded-radius-sm border border-line bg-bg px-3 py-2">
          <div className="font-medium text-ink">Clinic rating of you: {b.evaluation.average} / 5</div>
          <div className="text-xs text-ink-soft mt-0.5">
            Professionalism {b.evaluation.professionalism} · Ethical {b.evaluation.ethicalCode} · Time {b.evaluation.timeRespect} · Comm {b.evaluation.communication}
          </div>
          {b.evaluation.comment && <p className="text-ink-soft mt-1">{b.evaluation.comment}</p>}
        </div>
      )}
      {b.canRateClinic && b.phase === 'past' && !openRate && (
        <Button size="sm" variant="secondary" onClick={() => setOpenRate(true)}>Rate this clinic</Button>
      )}
      {b.hasStudentRating && (
        <div className="text-xs text-ink-faint">You rated this clinic.</div>
      )}
      {openRate && (
        <div className="space-y-2 border-t border-line pt-3">
          <div className="grid grid-cols-3 gap-2">
            <Input label="Training" type="number" min={0} max={5} value={form.trainingQuality} onChange={e => setForm(p => ({ ...p, trainingQuality: e.target.value }))} />
            <Input label="Support" type="number" min={0} max={5} value={form.doctorSupport} onChange={e => setForm(p => ({ ...p, doctorSupport: e.target.value }))} />
            <Input label="Learning" type="number" min={0} max={5} value={form.learningExperience} onChange={e => setForm(p => ({ ...p, learningExperience: e.target.value }))} />
          </div>
          <Input label="Comment (optional)" value={form.comment} onChange={e => setForm(p => ({ ...p, comment: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={submitRating} loading={saving}>Submit rating</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpenRate(false)}>Cancel</Button>
          </div>
        </div>
      )}
      {msg && <p className="text-xs text-ink-soft">{msg}</p>}
    </Card>
  );
}

type InternCategory = 'upcoming' | 'finished' | 'cancelled';

function internCategory(b: Booking): InternCategory {
  if (b.status === 'cancelled' || b.phase === 'cancelled') return 'cancelled';
  if (b.phase === 'past' || b.status === 'completed') return 'finished';
  return 'upcoming';
}

function ClubCollapseGroup({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <details className="group rounded-radius border border-line bg-surface shadow-card overflow-hidden">
      <summary className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none list-none min-h-[48px] touch-manipulation [&::-webkit-details-marker]:hidden hover:bg-bg/70">
        <span className="font-medium text-ink">{title}</span>
        <span className="inline-flex items-center gap-2 shrink-0">
          <span className="min-w-[1.5rem] h-6 px-2 rounded-full bg-bg border border-line text-xs font-semibold text-ink tabular-nums inline-flex items-center justify-center">
            {count}
          </span>
          <ChevronDown className="w-4 h-4 text-ink-faint transition-transform group-open:rotate-180" />
        </span>
      </summary>
      <div className="border-t border-line p-2 space-y-2">
        {count === 0 ? (
          <p className="text-sm text-ink-soft px-2 py-2">None yet.</p>
        ) : (
          children
        )}
      </div>
    </details>
  );
}

function ClubUpdatesBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const eventsQ = useQuery({
    queryKey: ['events-club-notify'],
    queryFn: () =>
      api.get<{ events: EventItem[]; total: number; unreadCount?: number }>('/api/events?limit=5'),
    staleTime: 60_000,
  });
  const notifyQ = useQuery({
    queryKey: ['student-notifications'],
    queryFn: () =>
      api.get<{
        notifications: Array<{
          id: string;
          type: string;
          title: string;
          body: string | null;
          link: string | null;
          isRead: boolean;
          createdAt: string;
        }>;
        unreadCount: number;
      }>('/api/student/notifications?limit=12'),
    staleTime: 20_000,
    refetchInterval: 45_000,
  });

  const events = eventsQ.data?.events ?? [];
  const notifications = notifyQ.data?.notifications ?? [];
  const unreadCount = (eventsQ.data?.unreadCount ?? 0) + (notifyQ.data?.unreadCount ?? 0);

  type FeedItem =
    | { kind: 'notification'; id: string; title: string; body: string | null; link: string | null; createdAt: string; isRead: boolean }
    | { kind: 'event'; id: string; title: string; slug: string; photoUrl?: string | null; photoKind?: string | null; createdAt: string; isRead: boolean };

  const feed: FeedItem[] = [
    ...notifications.map(n => ({
      kind: 'notification' as const,
      id: n.id,
      title: n.title,
      body: n.body,
      link: n.link,
      createdAt: n.createdAt,
      isRead: n.isRead,
    })),
    ...events.map(e => ({
      kind: 'event' as const,
      id: e.id,
      title: e.title,
      slug: e.slug,
      photoUrl: e.photoUrl,
      photoKind: e.photoKind,
      createdAt: e.createdAt,
      isRead: e.isRead !== false,
    })),
  ].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 12);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markNotificationRead = async (id: string) => {
    try {
      await api.post(`/api/student/notifications/${id}/read`);
      await qc.invalidateQueries({ queryKey: ['student-notifications'] });
    } catch {
      // ignore — still navigate
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/api/student/notifications/read-all');
      await qc.invalidateQueries({ queryKey: ['student-notifications'] });
    } catch {
      // ignore
    }
  };

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex flex-col items-center justify-center gap-0.5 min-w-[52px] min-h-[52px] px-1 rounded-radius-sm text-ink hover:bg-teal-soft/60 transition-colors touch-manipulation"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unreadCount > 0 ? `Updates, ${unreadCount} unread` : 'Updates'}
      >
        <span className="relative inline-flex">
          <Bell className="w-5 h-5 text-teal" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-sand text-[9px] font-bold text-white flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </span>
        <span className="text-[10px] font-medium text-ink-soft">Updates</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Updates"
          className="absolute right-0 top-full mt-2 z-30 w-[min(100vw-2rem,22rem)] bg-surface border border-line rounded-2xl shadow-pop overflow-hidden"
        >
          <div className="px-3 py-2.5 border-b border-line flex items-center justify-between gap-2">
            <div className="font-heading font-semibold text-ink text-sm">Updates</div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-ink-faint">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </span>
              {(notifyQ.data?.unreadCount ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => { void markAllRead(); }}
                  className="text-[10px] font-semibold text-sand-strong hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {eventsQ.isLoading || notifyQ.isLoading ? (
            <PageLoader variant="inline" />
          ) : feed.length === 0 ? (
            <p className="p-4 text-sm text-ink-soft">No updates yet.</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto divide-y divide-line">
              {feed.map(item => (
                <li key={`${item.kind}-${item.id}`}>
                  {item.kind === 'notification' ? (
                    <Link
                      to={item.link || '/student'}
                      onClick={() => {
                        setOpen(false);
                        if (!item.isRead) void markNotificationRead(item.id);
                      }}
                      className={`flex items-start gap-2.5 px-3 py-2.5 transition-colors touch-manipulation ${
                        !item.isRead ? 'bg-sand-soft hover:bg-[#f8d5c6]' : 'bg-surface hover:bg-bg'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-teal-soft shrink-0 flex items-center justify-center mt-0.5">
                        <Bell className="w-4 h-4 text-teal" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink leading-snug">{item.title}</div>
                        {item.body ? (
                          <div className="text-[11px] text-ink-soft mt-0.5 line-clamp-2">{item.body}</div>
                        ) : null}
                        <div className="text-[10px] text-ink-faint mt-1">
                          {new Date(item.createdAt).toLocaleDateString('en-GB', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <Link
                      to={`/events/${item.slug}`}
                      state={{ from: '/student' }}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 transition-colors touch-manipulation ${
                        !item.isRead ? 'bg-sand-soft hover:bg-[#f8d5c6]' : 'bg-surface hover:bg-bg'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden bg-teal-soft shrink-0">
                        {item.photoUrl ? (
                          item.photoKind === 'video' ? (
                            <div className="w-full h-full flex items-center justify-center bg-ink/70">
                              <Play className="w-3.5 h-3.5 text-white ml-0.5" />
                            </div>
                          ) : (
                            <img src={item.photoUrl} alt="" className="w-full h-full object-cover" />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <CalendarDays className="w-4 h-4 text-teal" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink truncate">{item.title}</div>
                        <div className="text-[11px] text-ink-faint">
                          Club event ·{' '}
                          {new Date(item.createdAt).toLocaleDateString('en-GB', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </div>
                      </div>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-line p-2">
            <Link
              to="/events"
              state={{ from: '/student' }}
              onClick={() => setOpen(false)}
              className="flex items-center justify-center min-h-[40px] text-sm font-semibold text-sand-strong hover:bg-sand-soft rounded-radius-sm touch-manipulation"
            >
              Club events
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function ClubContent() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [showPendingApproval, setShowPendingApproval] = useState(false);

  useEffect(() => {
    if (me?.id && me.approvalStatus === 'pending' && pendingApprovalPromptIsOpen(me.id)) {
      setShowPendingApproval(true);
      return;
    }
    if (me?.approvalStatus !== 'pending') setShowPendingApproval(false);
  }, [me?.id, me?.approvalStatus]);
  const [refMsg, setRefMsg] = useState('');
  const [refWithdrawing, setRefWithdrawing] = useState(false);
  const [copied, setCopied] = useState(false);
  const subQ = useQuery({ queryKey: ['student-sub'], queryFn: () => api.get<{ subscription: Subscription | null }>('/api/student/subscription') });
  const bookingsQ = useQuery({
    queryKey: ['student-bookings'],
    queryFn: () => api.get<{ bookings: Booking[]; total: number }>('/api/bookings?limit=50'),
  });
  const consultsQ = useQuery({
    queryKey: ['student-consultations-club'],
    queryFn: () => api.get<{ consultations: Consultation[] }>('/api/consultations?limit=100'),
  });
  const blogsQ = useQuery({
    queryKey: ['blogs-club'],
    queryFn: () => api.get<{ posts: BlogPost[] }>('/api/blogs?limit=2'),
  });
  const certsQ = useQuery({
    queryKey: ['student-certificates'],
    queryFn: () => api.get<{ certificates: Certificate[] }>('/api/certificates'),
  });
  const refsQ = useQuery({
    queryKey: ['student-referrals'],
    queryFn: () => api.get<{ referral: ReferralSummary }>('/api/student/referrals'),
  });

  const sub = subQ.data?.subscription;
  const bookings = bookingsQ.data?.bookings ?? [];
  const consultations = consultsQ.data?.consultations ?? [];
  const posts = (blogsQ.data?.posts ?? []).slice(0, 2);
  const certificates = certsQ.data?.certificates ?? [];
  const referral = refsQ.data?.referral;

  const copyReferralCode = async () => {
    if (!referral?.code) return;
    try {
      await navigator.clipboard.writeText(referral.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const requestReferralPayout = async () => {
    setRefMsg('');
    setRefWithdrawing(true);
    try {
      const res = await api.post<{ amountEgp: number }>('/api/student/referrals/withdraw');
      setRefMsg(`Referral payout requested for ${egp(res.amountEgp)}.`);
      qc.invalidateQueries({ queryKey: ['student-referrals'] });
    } catch (err) {
      setRefMsg(err instanceof ApiError ? err.message : 'Referral payout failed.');
    } finally {
      setRefWithdrawing(false);
    }
  };

  const loading = subQ.isLoading || bookingsQ.isLoading || consultsQ.isLoading || blogsQ.isLoading;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {/* Student profile photo */}
          <div className="shrink-0">
            {me?.photoUrl ? (
              <img
                src={me.photoUrl}
                alt={me.fullName ?? 'Profile photo'}
                className="w-14 h-14 rounded-full object-cover border-2 border-teal/20 shadow-sm"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-teal-soft border-2 border-teal/20 flex items-center justify-center shadow-sm">
                <span className="text-xl font-bold text-teal">
                  {me?.fullName ? me.fullName.charAt(0).toUpperCase() : '?'}
                </span>
              </div>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink">
              Hi{me?.fullName ? `, ${me.fullName.split(' ')[0]}` : ''}
            </h1>
            <p className="mt-1 text-sm text-ink-soft">Your internships, questions, and plan live here.</p>
          </div>
        </div>
        <ClubUpdatesBell />
      </div>

      {me?.approvalStatus === 'pending' && showPendingApproval && me.id && (
        <PendingApprovalModal userId={me.id} onClose={() => setShowPendingApproval(false)} />
      )}

      {me?.restriction && (
        <Alert type="warning">
          Your account is restricted
          {me.restriction.blockInternships && me.restriction.blockConsultations
            ? ' from internships and mentor questions'
            : me.restriction.blockInternships
              ? ' from booking internships'
              : me.restriction.blockConsultations
                ? ' from mentor questions'
                : ''}
          {me.restriction.endsAt
            ? ` until ${new Date(me.restriction.endsAt).toLocaleDateString('en-GB')}.`
            : ' until an admin lifts it.'}
          {me.restriction.reason ? ` Reason: ${me.restriction.reason}` : ''}
        </Alert>
      )}

      {!subQ.isLoading && (
        sub && (sub.status === 'active' || sub.status === 'frozen' || sub.status === 'pending' || missedFirstWeek(sub)) ? (
          <Card className="p-5 sm:p-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-ink-faint mb-2">My plan</div>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-heading text-lg font-bold text-ink">{sub.planName ?? 'Plan'}</div>
                <div className="mt-1"><StatusBadge status={sub.status} /></div>
              </div>
              <Link to="/plans" className="text-sm text-sand-strong font-medium hover:underline shrink-0">
                See plans
              </Link>
            </div>
            {waitingToStart(sub) && (
              <p className="text-sm text-ink-soft mt-3">
                Paying did not start your weeks. Booking opens 20 October 2026. You then have 14 days to book your first clinic week — that is when your plan time starts. If you buy after 20 October, the 14 days start the day you pay.
              </p>
            )}
            {missedFirstWeek(sub) && (
              <p className="text-sm text-ink-soft mt-3">
                Booking is locked — you missed the 14 days to start. Message us to reopen your plan.
              </p>
            )}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-xl bg-bg border border-line px-3 py-2.5">
                <div className="text-xs text-ink-faint">Weeks left</div>
                <div className="font-heading font-semibold text-ink tabular-nums">{sub.internshipCreditsRemaining}</div>
              </div>
              <div className="rounded-xl bg-bg border border-line px-3 py-2.5">
                <div className="text-xs text-ink-faint">Questions left</div>
                <div className="font-heading font-semibold text-ink tabular-nums">{sub.consultationCreditsRemaining}</div>
              </div>
              <div className="rounded-xl bg-bg border border-line px-3 py-2.5 col-span-2 sm:col-span-1">
                <div className="text-xs text-ink-faint">{planTimingLabel(sub)}</div>
                <div className="font-heading font-semibold text-ink">
                  {planTimingValue(sub)}
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-5 sm:p-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-ink-faint mb-2">My plan</div>
            <h2 className="font-heading text-lg font-bold text-ink mb-2">You’re not subscribed yet</h2>
            <p className="text-sm text-ink-soft mb-1">
              Clinic weeks and mentor questions wait on a plan.
            </p>
            <p className="text-sm font-semibold text-sand-strong mb-3">Limited 30% off applied.</p>
            <Link
              to="/plans"
              className="inline-flex w-full items-center justify-center gap-2 font-medium rounded-radius-sm text-sm px-4 py-3 min-h-[48px] bg-sand text-white hover:opacity-90 touch-manipulation"
            >
              See plans
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Card>
        )
      )}

      {loading ? <PageLoader variant="lines" /> : (
        <>
          {sub?.status === 'active' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/clinics" className="block group">
                <Card className="p-5 flex items-center gap-4 hover:shadow-pop transition-shadow h-full min-h-[88px]">
                  <div className="w-12 h-12 rounded-xl bg-teal-soft flex items-center justify-center shrink-0">
                    <Building2 className="w-6 h-6 text-teal" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink group-hover:text-teal transition-colors">Intern</div>
                    <div className="text-xs text-ink-soft">{sub.internshipCreditsRemaining} weeks left</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-ink-faint shrink-0" />
                </Card>
              </Link>
              <Link to="/doctors" className="block group">
                <Card className="p-5 flex items-center gap-4 hover:shadow-pop transition-shadow h-full min-h-[88px]">
                  <div className="w-12 h-12 rounded-xl bg-sand-soft flex items-center justify-center shrink-0">
                    <MessageCircle className="w-6 h-6 text-sand" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink group-hover:text-sand transition-colors">Ask mentor</div>
                    <div className="text-xs text-ink-soft">{sub.consultationCreditsRemaining} questions left</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-ink-faint shrink-0" />
                </Card>
              </Link>
            </div>
          )}

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-bold text-ink">My internships</h2>
              <div className="flex items-center gap-3">
                <WhatsAppHelp compact label="Help" />
                {sub?.status === 'active' && (
                  <Link to="/clinics" className="text-sm text-sand-strong font-medium hover:underline">Intern</Link>
                )}
              </div>
            </div>
            <div className="space-y-2">
                {([
                  { title: 'Upcoming', key: 'upcoming' as const, dir: 'asc' as const },
                  { title: 'Finished', key: 'finished' as const, dir: 'desc' as const },
                  { title: 'Cancelled', key: 'cancelled' as const, dir: 'desc' as const },
                ]).map(group => {
                  const rows = bookings
                    .filter(b => internCategory(b) === group.key)
                    .sort((a, b) => {
                      const cmp = (a.weekStartDate || '').localeCompare(b.weekStartDate || '');
                      return group.dir === 'asc' ? cmp : -cmp;
                    });
                  return (
                    <ClubCollapseGroup key={group.key} title={group.title} count={rows.length}>
                      {rows.map(b => <StudentBookingCard key={b.id} booking={b} />)}
                    </ClubCollapseGroup>
                  );
                })}
                {bookings.length === 0 && <WhatsAppHelp />}
              </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-bold text-ink">My questions</h2>
              <Link to="/doctors" className="text-sm text-sand-strong font-medium hover:underline">Ask</Link>
            </div>
            <div className="space-y-2">
                {([
                  { title: 'Answered', key: 'answered' },
                  { title: 'Pending', key: 'pending' },
                  { title: 'Under review', key: 'under_review' },
                  { title: 'Declined', key: 'declined' },
                  { title: 'Expired', key: 'expired' },
                ] as const).map(group => {
                  const rows = consultations
                    .filter(c => c.status === group.key)
                    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
                  if (rows.length === 0 && (group.key === 'under_review' || group.key === 'declined')) {
                    return null;
                  }
                  return (
                    <ClubCollapseGroup key={group.key} title={group.title} count={rows.length}>
                      {rows.map(c => (
                        <Link key={c.id} to={`/student/consultations/${c.id}`} className="block">
                          <Card className="p-4 hover:shadow-card transition-shadow">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <StatusBadge status={c.status} />
                              <span className="text-xs text-ink-faint">{new Date(c.createdAt).toLocaleDateString('en-GB')}</span>
                            </div>
                            <p className="text-sm text-ink line-clamp-2">{c.question}</p>
                          </Card>
                        </Link>
                      ))}
                    </ClubCollapseGroup>
                  );
                })}
              </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-bold text-ink">Learn</h2>
              <Link to="/blogs" className="text-sm text-sand-strong font-medium hover:underline">See all</Link>
            </div>
            {posts.length === 0 ? (
              <p className="text-sm text-ink-soft">No posts yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {posts.map(post => (
                  <Link key={post.id} to={`/blogs/${post.id}`} className="block group">
                    <Card className="overflow-hidden hover:shadow-card transition-shadow h-full">
                      <div className="bg-teal-soft overflow-hidden">
                        {post.coverUrl ? (
                          <SmartCoverMedia
                            src={post.coverUrl}
                            kind={post.coverKind}
                            alt={post.title}
                            learnFrame
                            playable={false}
                            poster={post.thumbnailUrl}
                          />
                        ) : (
                          <div className="aspect-[16/9] w-full flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-teal" />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        {post.category?.name && (
                          <div className="text-[10px] text-ink-faint mb-0.5 truncate">{post.category.name}</div>
                        )}
                        <div className="font-medium text-ink text-sm group-hover:text-teal transition-colors line-clamp-2">{post.title}</div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {refMsg && (
            <Alert type={refMsg.toLowerCase().includes('fail') ? 'error' : 'success'}>{refMsg}</Alert>
          )}

          {certificates.length > 0 && (
            <section className="space-y-3">
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4 text-teal" />
                  <div className="font-medium text-ink">Certificates</div>
                </div>
                <div className="text-2xl font-bold text-ink">{certificates.length}</div>
              </Card>
            </section>
          )}

          {referral && (
            <section className="space-y-3">
              <Card className="p-4 sm:p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-sand" />
                    <div className="font-medium text-ink">Your referral code</div>
                  </div>
                  <p className="text-sm text-ink-soft">
                    Share this code. You earn when someone signs up with it and their account is approved, and again when they buy their first plan.
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-lg font-bold text-sand flex-1 min-w-0 break-all">{referral.code}</div>
                    <Button size="sm" variant="ghost" onClick={copyReferralCode}>
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="rounded-radius-sm border border-line bg-bg p-3">
                      <div className="text-xs text-ink-soft">Accounts approved</div>
                      <div className="text-lg font-bold text-ink">{referral.registeredCount}</div>
                    </div>
                    <div className="rounded-radius-sm border border-line bg-bg p-3">
                      <div className="text-xs text-ink-soft">Plans bought</div>
                      <div className="text-lg font-bold text-ink">{referral.subscribedCount}</div>
                    </div>
                    <div className="rounded-radius-sm border border-line bg-bg p-3">
                      <div className="text-xs text-ink-soft">Total earned</div>
                      <div className="text-lg font-bold text-ink">{egp(referral.totalEarnedEgp)}</div>
                    </div>
                    <div className="rounded-radius-sm border border-line bg-bg p-3">
                      <div className="text-xs text-ink-soft">Available</div>
                      <div className="text-lg font-bold text-ink">{egp(referral.availableEgp)}</div>
                    </div>
                  </div>
                  {referral.availableEgp > 0 && (
                    <Button size="sm" onClick={requestReferralPayout} loading={refWithdrawing}>
                      Request referral payout ({egp(referral.availableEgp)})
                    </Button>
                  )}

                  <div className="pt-1 space-y-2">
                    <div className="font-medium text-ink text-sm">People who used your code</div>
                    <Table
                      data={referral.activity}
                      empty="No one has used your code yet."
                      getRowKey={row => row.id}
                      columns={[
                        {
                          key: 'name',
                          header: 'Name',
                          render: row => (
                            <span className="font-medium text-ink">{row.displayName}</span>
                          ),
                        },
                        {
                          key: 'action',
                          header: 'Action',
                          render: row => row.action,
                        },
                        {
                          key: 'earned',
                          header: 'You earned',
                          render: row => (
                            <span className="font-semibold text-ink">{egp(row.rewardEgp)}</span>
                          ),
                        },
                        {
                          key: 'status',
                          header: 'Status',
                          render: row => <StatusBadge status={row.status} />,
                        },
                        {
                          key: 'date',
                          header: 'Date',
                          render: row =>
                            new Date(row.createdAt).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            }),
                        },
                      ]}
                    />
                  </div>
                </Card>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export function StudentDashboardPage() {
  return (
    <Layout>
      <ClubContent />
    </Layout>
  );
}

// ─── Sub-pages (separate routes, not tabs) ────────────────────────────────────

export function CheckoutPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState<null | { valid: boolean; discountType?: string; discountValue?: number; message?: string }>(null);
  const [method, setMethod] = useState<'instapay' | 'card' | 'wallet'>('instapay');
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const publicSettingsQ = useSupportPhone();
  const pePolicy = publicSettingsQ.data?.physioedgePolicy
    ?? 'By paying, you agree to PhysioEDGE terms for internship weeks and mentor questions.';
  const instapayRecipient = (publicSettingsQ.data?.instapayRecipient ?? '').trim();
  const instapayPayUrl = (publicSettingsQ.data?.instapayPayUrl ?? '').trim();
  const instapayAccountName = (publicSettingsQ.data?.instapayAccountName ?? '').trim();

  const planQ = useQuery({ queryKey: ['plan', planId], queryFn: () => api.get<{ plans: Plan[] }>('/api/plans').then(d => d.plans.find(p => p.id === planId)) });
  const plan = planQ.data;

  const promoDiscountEgp = (() => {
    if (!plan || !promoStatus?.valid || promoStatus.discountValue == null) return 0;
    if (promoStatus.discountType !== 'percent' && promoStatus.discountType !== 'flat') return 0;
    return applyDiscount(plan.priceEgp, promoStatus.discountType, promoStatus.discountValue).discountEgp;
  })();
  const payAmountEgp = plan ? Math.max(0, plan.priceEgp - promoDiscountEgp) : 0;

  const validatePromo = async (code = promoCode) => {
    const trimmed = code.trim();
    if (!trimmed || !planId) {
      setPromoStatus(null);
      return { valid: false as const };
    }
    try {
      const res = await api.post<{ valid: boolean; discountType?: string; discountValue?: number; message?: string }>('/api/promo-codes/validate', { code: trimmed, planId });
      setPromoStatus(res);
      return res;
    } catch {
      const failed = { valid: false, message: 'Error checking code.' };
      setPromoStatus(failed);
      return failed;
    }
  };

  const checkout = async () => {
    if (!policyAccepted) {
      setError('Accept the PhysioEDGE policy to continue.');
      return;
    }
    if (method !== 'instapay') {
      setError('Card and wallet payments are coming soon. Please pay with InstaPay.');
      return;
    }
    if (promoCode.trim()) {
      const promo = await validatePromo();
      if (!promo.valid) {
        setError(promo.message ?? 'That promo code is not valid.');
        return;
      }
    }
    if (method === 'instapay' && !proofFile) {
      setError('Attach a screenshot of your InstaPay transfer.');
      return;
    }
    setError(''); setLoading(true);
    try {
      const fd = new FormData();
      fd.append('planId', planId!);
      fd.append('paymentMethod', method);
      if (promoCode.trim()) fd.append('promoCode', promoCode.trim());
      fd.append('proof', proofFile!);
      const res = await api.post<{ checkoutUrl: string | null; paymentId: string; message?: string; resumed?: boolean }>(
        '/api/payments/checkout',
        fd,
      );
      if (res.resumed && res.message) {
        setError(res.message);
      }
      if (res.checkoutUrl) {
        try {
          const next = new URL(res.checkoutUrl, window.location.origin);
          if (next.pathname.startsWith('/payment/return')) {
            navigate(`${next.pathname}${next.search}`);
            return;
          }
          window.location.href = next.href;
        } catch {
          window.location.href = res.checkoutUrl;
        }
        return;
      }
      navigate(`/payment/return?paymentId=${res.paymentId}&awaiting=instapay`);
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Checkout failed.');
      setLoading(false);
    }
  };

  if (!plan) return <PageLoader variant="form" />;

  return (
    <Layout>
      <PageHeader title="Checkout" subtitle={plan.name} />
      <div className="max-w-md">
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <Card className="p-6 mb-4 relative overflow-hidden">
          <PlanOfferBadge />
          <div className="space-y-2 text-sm mb-4 pt-14">
            <div className="flex justify-between font-semibold text-ink">
              <span>{plan.name}</span><span>{egp(plan.priceEgp)}</span>
            </div>
            {promoDiscountEgp > 0 && (
              <div className="flex justify-between text-success">
                <span>Promo discount</span>
                <span>-{egp(promoDiscountEgp)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-ink border-t border-line pt-2">
              <span>Amount to send</span>
              <span>{egp(payAmountEgp)}</span>
            </div>
          </div>
          <p className="text-sm font-semibold text-sand-strong mb-3">Limited 30% off applied.</p>
          <div className="flex gap-2 mb-1 items-end">
            <Input
              className="flex-1"
              label="Promo code (optional)"
              value={promoCode}
              onChange={e => {
                setPromoCode(e.target.value.toUpperCase());
                setPromoStatus(null);
              }}
              onBlur={() => { void validatePromo(); }}
            />
            <Button variant="ghost" size="sm" onClick={() => { void validatePromo(); }} type="button">Apply</Button>
          </div>
          <p className="text-xs text-ink-faint mb-2">Optional discount code — different from a friend’s referral at signup.</p>
          {promoStatus && (
            <Alert type={promoStatus.valid ? 'success' : 'error'}>
              {promoStatus.valid ? 'Promo code applied!' : promoStatus.message}
            </Alert>
          )}
        </Card>
        <Card className="p-6 mb-4">
          <h3 className="text-sm font-medium text-ink mb-3">Payment method</h3>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setMethod('instapay')}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-radius-sm border text-left transition-colors ${
                method === 'instapay' ? 'border-sand bg-sand-soft text-sand-strong' : 'border-line text-ink-soft hover:text-ink'
              }`}
            >
              <img src="/instapay.svg" alt="" className="h-7 w-auto shrink-0 rounded" />
              <span className="text-sm font-medium text-ink">InstaPay</span>
            </button>
            {([
              { key: 'card' as const, label: 'Card', Icon: CreditCard },
              { key: 'wallet' as const, label: 'Wallet', Icon: Wallet },
            ]).map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                disabled
                className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-radius-sm border border-line text-ink-faint cursor-not-allowed opacity-70"
              >
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  <Icon className="w-4 h-4" />
                  Paymob {label}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-wide">Coming soon</span>
              </button>
            ))}
          </div>
          {method === 'instapay' && (
            <div className="mt-4 space-y-3 text-sm text-ink-soft">
              <p>
                Send <span className="font-semibold text-ink">{egp(payAmountEgp)}</span> to{' '}
                <span className="font-semibold text-ink">
                  {instapayRecipient || '…..'}
                </span>
                .
              </p>
              {instapayAccountName && (
                <p>
                  Confirm the account name is{' '}
                  <span className="font-semibold text-ink">{instapayAccountName}</span>{' '}
                  before you send.
                </p>
              )}
              {instapayPayUrl && (
                <p>
                  <a
                    href={instapayPayUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline font-medium break-all"
                  >
                    {instapayPayUrl}
                  </a>
                </p>
              )}
              <p>Your plan will be activated within 4 hours after we confirm the transfer.</p>
              <div>
                <label className="block text-xs font-medium text-ink mb-1.5">
                  Transfer screenshot *
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={e => setProofFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-ink-soft file:mr-3 file:rounded-radius-sm file:border-0 file:bg-teal-soft file:px-3 file:py-2 file:text-sm file:font-medium file:text-teal"
                  required
                />
                <p className="text-xs text-ink-faint mt-1.5">
                  Attach a screenshot of the InstaPay transaction. Required to submit.
                </p>
                {proofFile && (
                  <p className="text-xs text-success mt-1 truncate">{proofFile.name}</p>
                )}
              </div>
            </div>
          )}
        </Card>
        <Card className="p-6 mb-4">
          <label className="flex items-start gap-3 text-sm text-ink cursor-pointer">
            <input
              type="checkbox"
              checked={policyAccepted}
              onChange={e => setPolicyAccepted(e.target.checked)}
              className="mt-1 rounded border-line"
            />
            <span>
              <span className="font-medium">I agree to the PhysioEDGE policy and these purchase terms.</span>
              <FormattedText text={pePolicy} className="mt-1 text-xs text-ink-soft" />
              <ul className="mt-2 text-xs text-ink-soft space-y-1.5 list-disc pl-4">
                <li>
                  After you pay, you have 14 days to book your first clinic week
                  (that window starts on 20 October 2026 if you buy before that date).
                  Your plan length starts only when that first week is booked.
                </li>
                <li>
                  If the first week is not booked in time, the plan expires and remaining weeks and questions cannot be used unless we reopen it for you.
                </li>
                <li>
                  All payments are non-refundable. If you have exceptional circumstances, contact PhysioEDGE and we will review your request.
                </li>
              </ul>
            </span>
          </label>
        </Card>
        <Button
          onClick={checkout}
          loading={loading}
          disabled={
            !policyAccepted
            || method !== 'instapay'
            || !proofFile
          }
          className="w-full"
          size="lg"
        >
          {method === 'instapay' ? `I’ve sent ${egp(payAmountEgp)}` : `Pay ${egp(payAmountEgp)} →`}
        </Button>
        <div className="mt-4 text-center">
          <WhatsAppHelp label="WhatsApp us for help" />
        </div>
      </div>
    </Layout>
  );
}

export function PaymentReturnPage() {
  const search = new URLSearchParams(window.location.search);
  const paymentId = search.get('paymentId');
  const awaitingInstapay = search.get('awaiting') === 'instapay';
  const qc = useQueryClient();
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofBusy, setProofBusy] = useState(false);
  const [proofMsg, setProofMsg] = useState('');
  const pollQ = useQuery({
    queryKey: ['payment-status', paymentId],
    queryFn: () => api.get<{
      status: string;
      subscriptionStatus: string | null;
      method: string | null;
      failureReason: string | null;
      hasProof: boolean;
      proofUrl: string | null;
    }>(`/api/payments/status/${paymentId}`),
    refetchInterval: d => {
      const status = d.state.data?.status;
      if (!status || status === 'pending') {
        return awaitingInstapay || d.state.data?.method === 'instapay' ? false : 2000;
      }
      return false;
    },
    enabled: !!paymentId,
  });

  const status = pollQ.data?.status;
  const method = pollQ.data?.method;
  const failureReason = pollQ.data?.failureReason;
  const hasProof = !!pollQ.data?.hasProof;
  const isInstapayPending = (awaitingInstapay || method === 'instapay') && (!status || status === 'pending');

  const uploadProof = async () => {
    if (!paymentId || !proofFile) return;
    setProofBusy(true);
    setProofMsg('');
    try {
      const fd = new FormData();
      fd.append('proof', proofFile);
      await api.post(`/api/payments/${paymentId}/instapay-proof`, fd);
      setProofFile(null);
      setProofMsg('Screenshot uploaded.');
      await qc.invalidateQueries({ queryKey: ['payment-status', paymentId] });
    } catch (err) {
      setProofMsg(err instanceof ApiError ? err.friendlyMessage : 'Could not upload screenshot.');
    } finally {
      setProofBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <Card className="w-full max-w-sm p-8 text-center">
        {isInstapayPending ? (
          <>
            <img src="/instapay.svg" alt="InstaPay" className="h-8 mx-auto mb-4 rounded" />
            <h2 className="font-heading text-xl font-bold text-ink mb-2">We’re reviewing your InstaPay</h2>
            <p className="text-ink-soft text-sm mb-4">
              Your plan will be activated within 4 hours after we confirm the transfer.
            </p>
            {!hasProof ? (
              <div className="text-left mb-4 space-y-2 rounded-radius-sm border border-line bg-bg p-3">
                <div className="text-sm font-medium text-ink">Transfer screenshot required</div>
                <p className="text-xs text-ink-soft">
                  Upload a screenshot of the InstaPay transaction so we can confirm your payment.
                </p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={e => setProofFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-ink-soft file:mr-3 file:rounded-radius-sm file:border-0 file:bg-teal-soft file:px-3 file:py-2 file:text-sm file:font-medium file:text-teal"
                  required
                />
                <Button
                  size="sm"
                  className="w-full"
                  loading={proofBusy}
                  disabled={!proofFile}
                  onClick={() => { void uploadProof(); }}
                >
                  Upload screenshot
                </Button>
                {proofMsg && (
                  <p className={`text-xs ${proofMsg.includes('uploaded') ? 'text-success' : 'text-danger'}`}>{proofMsg}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-success mb-4">Screenshot received — thanks.</p>
            )}
            {hasProof && (
              <Link to="/student?tab=subscription" className="text-sand-strong text-sm font-medium hover:underline">
                Back to subscription →
              </Link>
            )}
          </>
        ) : !status || status === 'pending' ? (
          <PageLoader variant="inline" />
        ) : status === 'paid' ? (
          <>
            <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-success mb-2">Payment confirmed</h2>
            <p className="text-ink-soft text-sm mb-4">Your subscription is now active.</p>
            <Link to="/student?tab=subscription" className="text-sand-strong text-sm font-medium hover:underline">View subscription →</Link>
          </>
        ) : (
          <>
            <XCircle className="w-12 h-12 text-danger mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-danger mb-2">Payment failed</h2>
            <p className="text-ink-soft text-sm mb-4">{failureReason || 'Please try again.'}</p>
            <Link to="/student?tab=subscription" className="text-sand-strong text-sm font-medium hover:underline">Back to subscription →</Link>
          </>
        )}
      </Card>
    </div>
  );
}

export function NewBookingPage() {
  return <Navigate to="/clinics" replace />;
}

export function NewConsultationPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const doctorId = searchParams.get('doctorId') ?? '';
  const [question, setQuestion] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPhotoGate, setShowPhotoGate] = useState(false);

  const doctorQ = useQuery({
    queryKey: ['doctor', doctorId],
    queryFn: () => api.get<{ doctor: Doctor }>(`/api/doctors/${doctorId}`),
    enabled: !!doctorId,
  });

  const subQ = useQuery({
    queryKey: ['student-sub'],
    queryFn: () => api.get<{ subscription: Subscription | null }>('/api/student/subscription'),
  });

  const doctor = doctorQ.data?.doctor;
  const sub = subQ.data?.subscription;
  const questionsLeft = sub?.consultationCreditsRemaining ?? 0;
  const needsPlan = subQ.isFetched && (!sub || sub.status !== 'active' || questionsLeft <= 0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doctorId || !doctor) {
      setError('Please choose a doctor first.');
      return;
    }
    if (!doctor.acceptingConsultations) {
      setError('This mentor is not accepting questions right now.');
      return;
    }
    const trimmed = question.trim();
    if (trimmed.length < 10) {
      setError('Please write at least 10 characters so the doctor has enough context.');
      return;
    }
    if (!me?.hasPhoto) {
      setShowPhotoGate(true);
      return;
    }
    setError(''); setLoading(true);
    try {
      await api.post('/api/consultations', { doctorId, question: trimmed });
      navigate('/student');
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Failed to submit. Please try again.');
    }
    finally { setLoading(false); }
  };

  const submitAfterPhoto = async () => {
    setShowPhotoGate(false);
    setError(''); setLoading(true);
    try {
      await api.post('/api/consultations', { doctorId, question: question.trim() });
      navigate('/student');
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Failed to submit. Please try again.');
    }
    finally { setLoading(false); }
  };

  if (!doctorId) {
    return (
      <Layout>
        <PageHeader title="Ask your mentor" />
        <Card className="p-8 text-center max-w-md">
          <div className="w-14 h-14 rounded-full bg-teal-soft flex items-center justify-center mx-auto mb-4">
            <Stethoscope className="w-7 h-7 text-teal" />
          </div>
          <h3 className="font-heading font-semibold text-lg text-ink mb-2">Pick a doctor first</h3>
          <p className="text-sm text-ink-soft mb-5">Find a doctor first, then ask your question on their page.</p>
          <Link
            to="/doctors"
            className="bg-sand text-white px-6 py-3.5 rounded-radius-sm text-sm font-semibold hover:opacity-90 inline-flex items-center justify-center min-h-[48px] touch-manipulation"
          >
            Find a doctor
          </Link>
        </Card>
      </Layout>
    );
  }

  if (subQ.isLoading || doctorQ.isLoading) {
    return (
      <Layout>
        <PageHeader title="Ask your mentor" />
        <PageLoader variant="form" />
      </Layout>
    );
  }

  if (needsPlan) {
    return (
      <Layout>
        <PageHeader title="Ask your mentor" />
        <div className="mb-8 text-center">
          <div className="w-16 h-16 rounded-full bg-teal-soft flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-teal" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-ink mb-2">
            {!sub || sub.status !== 'active' ? 'You need a plan' : 'No questions left'}
          </h2>
          <p className="text-sm text-ink-soft max-w-md mx-auto">
            {!sub || sub.status !== 'active'
              ? 'You need a plan for clinic weeks and mentor questions.'
              : 'You’re out of questions. Choose a new plan to keep asking.'}
          </p>
        </div>
        <InlinePlans />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader title="Ask your mentor" />
      <div className="max-w-lg space-y-4">
        {error && <Alert type="error">{error}</Alert>}

        {!doctor ? (
          <Alert type="error">Doctor not found. <Link to="/doctors" className="underline">Browse doctors</Link>.</Alert>
        ) : !doctor.acceptingConsultations ? (
          <Card className="p-8 text-center max-w-md">
            <h3 className="font-heading font-semibold text-lg text-ink mb-2">This mentor is not accepting questions</h3>
            <p className="text-sm text-ink-soft mb-5">Asking is paused for this mentor right now. Try another doctor, or check back later.</p>
            <Link
              to="/doctors"
              className="bg-sand text-white px-6 py-3.5 rounded-radius-sm text-sm font-semibold hover:opacity-90 inline-flex items-center justify-center min-h-[48px] touch-manipulation"
            >
              Find another doctor
            </Link>
          </Card>
        ) : (
          <>
            <Card className="p-5 flex items-center gap-4">
              {doctor.photoUrl ? (
                <img src={doctor.photoUrl} alt={doctor.fullName} className="w-16 h-16 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-teal-soft flex items-center justify-center shrink-0">
                  <span className="text-2xl font-bold text-teal">{doctor.fullName.charAt(0)}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-ink text-lg leading-tight">
                  {doctor.fullName.startsWith('Dr.') ? doctor.fullName : `Dr. ${doctor.fullName}`}
                </div>
                {doctor.specialty && <div className="text-sm text-ink-soft mt-0.5">{doctor.specialty}</div>}
                <div className="text-sm font-medium text-teal mt-1">
                  Costs 1 question · {questionsLeft} left
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <form onSubmit={submit} className="space-y-4">
                <Textarea
                  label="Your question"
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                  placeholder="I am a second year student and overwhelmed with the curriculum. I can't relate the practical hands-on to the theoretical knowledge."
                  rows={7}
                  required
                  minLength={10}
                />
                <div className="flex items-center justify-between gap-3 text-xs text-ink-faint">
                  <span>At least 10 characters · costs 1 question</span>
                  <span className={question.trim().length > 0 && question.trim().length < 10 ? 'text-amber-600 font-medium' : ''}>
                    {question.trim().length}/3000
                  </span>
                </div>
                <Button
                  type="submit"
                  loading={loading}
                  disabled={question.trim().length < 10}
                  className="w-full"
                  size="lg"
                >
                  Send question
                </Button>
              </form>
            </Card>
          </>
        )}
      </div>
      <RequirePhotoModal
        open={showPhotoGate}
        onClose={() => setShowPhotoGate(false)}
        onDone={submitAfterPhoto}
      />
    </Layout>
  );
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function ConsultationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const cQ = useQuery({
    queryKey: ['consultation', id],
    queryFn: () => api.get<{ consultation: Consultation }>(`/api/consultations/${id}`),
    enabled: !!id,
  });

  const c = cQ.data?.consultation;

  return (
    <Layout>
      <button
        type="button"
        onClick={() => navigate('/student?tab=consultations')}
        className="text-sm text-ink-soft hover:text-ink mb-4 inline-flex items-center gap-1"
      >
        ← Back to consultations
      </button>

      {cQ.isLoading ? <PageLoader variant="detail" /> : !c ? (
        <Card className="p-8 text-center">
          <h3 className="font-semibold text-ink mb-2">Consultation not found</h3>
          <p className="text-sm text-ink-soft mb-4">This question may have been removed or you don’t have access.</p>
          <Link to="/student?tab=consultations" className="text-sand-strong text-sm font-medium hover:underline">
            Back to consultations
          </Link>
        </Card>
      ) : (
        <div className="max-w-2xl space-y-4">
          <PageHeader title="Consultation" />
          <Card className="p-5 sm:p-6 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs text-ink-faint uppercase tracking-wide mb-1">Doctor</div>
                <div className="font-heading text-lg font-semibold text-ink">{c.doctorName}</div>
              </div>
              <StatusBadge status={c.status} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-ink-faint mb-0.5">Sent</div>
                <div className="font-medium text-ink">{formatDateTime(c.createdAt)}</div>
              </div>
              <div>
                <div className="text-xs text-ink-faint mb-0.5">
                  {c.status === 'answered' ? 'Answered' : 'Deadline'}
                </div>
                <div className="font-medium text-ink">
                  {c.status === 'answered' && c.answeredAt
                    ? formatDateTime(c.answeredAt)
                    : formatDateTime(c.responseDeadline)}
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-2">Your question</div>
              <div className="rounded-radius-sm bg-bg border border-line p-4 text-sm text-ink whitespace-pre-wrap leading-relaxed">
                {c.question}
              </div>
            </div>

            {c.status === 'answered' && c.answer ? (
              <div>
                <div className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-2">Doctor’s answer</div>
                <div className="rounded-radius-sm bg-success-soft/40 border border-success/20 p-4 text-sm text-ink whitespace-pre-wrap leading-relaxed">
                  {c.answer}
                </div>
              </div>
            ) : c.status === 'under_review' ? (
              <Alert type="info">
                {c.questionModerationStatus === 'pending_review'
                  ? 'PhysioEDGE is reviewing your question before it reaches the mentor.'
                  : 'The mentor answered — PhysioEDGE is reviewing the answer before you can read it.'}
                {c.moderationNote ? ` Note: ${c.moderationNote}` : ''}
              </Alert>
            ) : c.status === 'declined' ? (
              <Alert type="warning">
                This question was not approved by PhysioEDGE.
                {c.moderationNote ? ` Reason: ${c.moderationNote}` : ''} Your consultation credit was returned.
              </Alert>
            ) : c.status === 'pending' ? (
              <Alert type="info">
                Waiting for {c.doctorName} to answer. Deadline {formatDateTime(c.responseDeadline)}.
              </Alert>
            ) : c.status === 'expired' ? (
              <Alert type="warning">
                This question expired without an answer. The consultation credit was not returned.
              </Alert>
            ) : null}
          </Card>
        </div>
      )}
    </Layout>
  );
}
